// Mock data for Full Bench — a product by The Bench

export const BRAND = {
  name: "Full Bench",
  parent: "The Bench",
  tagline: "Plan, manage and monitor every project. Bill for every hour.",
  subtitle: "Full Bench is the complete kit for consulting professionals who bill on time and expense.",
};

export const NAV_LINKS = [
  { label: "Features", href: "/features" },
  { label: "Pricing", href: "/pricing" },
  { label: "Customers", href: "/customers" },
  { label: "Compare", href: "/compare" },
];

export const PILLARS = [
  { k: "Plan", v: "Set up projects, budgets and rates in minutes." },
  { k: "Manage", v: "Track every billable hour with a timer that stays out of the way." },
  { k: "Monitor", v: "See burn, margin and utilisation before the month closes." },
];

export const HERO_STATS = [
  { k: "1 timer", v: "from browser, desktop or phone" },
  { k: "< 60 sec", v: "to send an invoice from tracked hours" },
  { k: "Every $", v: "of your time, accounted for" },
];

export const FEATURES = [
  {
    icon: "Timer",
    title: "Track every billable hour",
    body: "Start a timer in one click from your browser, desktop or phone. Log time against clients, projects and tasks in seconds — nothing slips through.",
  },
  {
    icon: "FolderKanban",
    title: "Plan projects and budgets",
    body: "Set up a project, rates and an hour budget in under a minute. Add tasks, assign team members, and know exactly what a job is worth before you start it.",
  },
  {
    icon: "Receipt",
    title: "Invoice in one click",
    body: "Every tracked hour becomes a line item. Send professional invoices, accept Stripe and PayPal, and reconcile against projects without leaving the app.",
  },
  {
    icon: "BarChart3",
    title: "Monitor burn and margin",
    body: "Live dashboards show budget burn, billable ratio and revenue per project. Spot an over-run before it becomes a conversation with your client.",
  },
  {
    icon: "Users",
    title: "Manage the whole team",
    body: "Approvals, capacity and reminders keep every consultant on the same page. See who is under-utilised and who is heading for a busy week.",
  },
  {
    icon: "PlugZap",
    title: "Fits your tech stack",
    body: "50+ integrations across Xero, MYOB, Asana, Jira, Teams and Slack. Or wire in whatever you use via our REST API.",
  },
];

export const LOGOS = ["Aurora Rail", "Meridian Water", "Kinsley Group", "Portside", "North Ridge", "Foundry Co."];

export const TESTIMONIALS = [
  {
    quote:
      "Full Bench turned our messy spreadsheets into a real system. Every hour has a home, and invoicing takes minutes instead of an afternoon.",
    name: "Maya Chen",
    role: "Commercial Lead, Aurora Rail",
  },
  {
    quote:
      "Our consultants actually track time now — the timer is that unobtrusive. And I can finally see project margin without waiting for month-end.",
    name: "Daniel Ortega",
    role: "Delivery Manager, Field & Form",
  },
  {
    quote:
      "We swapped a mess of timesheets and Xero exports for one tool. Month-end used to eat two days — now it takes three hours.",
    name: "Priya Ramaswamy",
    role: "Partner, Cedar Advisory",
  },
];

export const PRICING = [
  {
    name: "Starter",
    price: 0,
    period: "",
    tagline: "For a single consultant, tracking time on up to two projects.",
    features: [
      "1 seat",
      "2 projects",
      "Unlimited invoices",
      "Time tracking + reports",
      "iOS, Android, Mac, Windows apps",
    ],
    cta: "Get started free",
  },
  {
    name: "Team",
    price: 12,
    period: "per seat / month",
    tagline: "For consulting teams that plan, manage and monitor multiple projects.",
    features: [
      "Unlimited seats",
      "Unlimited projects",
      "Everything in Starter",
      "Team capacity + budgets",
      "Integrations with 50+ tools",
      "Priority support",
    ],
    cta: "Try Team free",
    featured: true,
  },
  {
    name: "Firm",
    price: 18,
    period: "per seat / month",
    tagline: "For firms that need SSO, retention and a named partner.",
    features: [
      "Everything in Team",
      "SSO (SAML)",
      "Advanced approvals",
      "Custom retention",
      "Dedicated success partner",
    ],
    cta: "Talk to us",
  },
];

// --- App data ---

export const CLIENTS = [
  { id: "c1", name: "Aurora Rail", color: "#22C55E" },
  { id: "c2", name: "Cedar Advisory", color: "#143A25" },
  { id: "c3", name: "Field & Form", color: "#0E9F6E" },
  { id: "c4", name: "Meridian Water", color: "#4ADE80" },
];

export const PROJECTS = [
  { id: "p1", name: "Station precinct bid", clientId: "c1", budgetHours: 120, spentHours: 78.5, rate: 285, billable: true },
  { id: "p2", name: "Contract review — D&C", clientId: "c1", budgetHours: 60, spentHours: 41, rate: 265, billable: true },
  { id: "p3", name: "Procurement strategy", clientId: "c2", budgetHours: 200, spentHours: 156.25, rate: 240, billable: true },
  { id: "p4", name: "Internal delivery playbook", clientId: "c3", budgetHours: 80, spentHours: 22, rate: 0, billable: false },
  { id: "p5", name: "Risk & claims support", clientId: "c4", budgetHours: 90, spentHours: 61.75, rate: 275, billable: true },
];

export const TASKS = [
  "Advisory",
  "Bid management",
  "Contract review",
  "Meetings",
  "Cost engineering",
  "Scheduling",
  "Project management",
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x.toISOString().slice(0, 10);
};

export const seedEntries = () => {
  const t = todayISO();
  return [
    { id: "e1", date: addDays(t, -4), projectId: "p1", task: "Advisory", notes: "Bid strategy workshop", hours: 3.5, billable: true },
    { id: "e2", date: addDays(t, -4), projectId: "p3", task: "Meetings", notes: "Client kickoff", hours: 1.25, billable: true },
    { id: "e3", date: addDays(t, -3), projectId: "p1", task: "Bid management", notes: "Program iterations", hours: 4, billable: true },
    { id: "e4", date: addDays(t, -3), projectId: "p5", task: "Contract review", notes: "Claims analysis", hours: 2, billable: true },
    { id: "e5", date: addDays(t, -2), projectId: "p2", task: "Contract review", notes: "Clause markup", hours: 3, billable: true },
    { id: "e6", date: addDays(t, -2), projectId: "p4", task: "Project management", notes: "Sprint planning", hours: 1.5, billable: false },
    { id: "e7", date: addDays(t, -1), projectId: "p3", task: "Cost engineering", notes: "Cost plan v2", hours: 2.75, billable: true },
    { id: "e8", date: addDays(t, -1), projectId: "p1", task: "Advisory", notes: "Risk register update", hours: 3, billable: true },
    { id: "e9", date: t, projectId: "p1", task: "Meetings", notes: "Client review", hours: 1.5, billable: true },
  ];
};

export const INVOICES = [
  { id: "INV-2026-014", clientId: "c1", status: "Paid", issued: "2026-06-04", due: "2026-06-18", total: 18410 },
  { id: "INV-2026-015", clientId: "c2", status: "Sent", issued: "2026-06-11", due: "2026-06-25", total: 26400 },
  { id: "INV-2026-016", clientId: "c4", status: "Draft", issued: "2026-06-14", due: "2026-06-28", total: 11265 },
  { id: "INV-2026-017", clientId: "c1", status: "Overdue", issued: "2026-05-15", due: "2026-05-29", total: 7480 },
];

// --- Comparison funnel ---

export const COMPETITORS = [
  {
    slug: "toggl-track",
    name: "Toggl Track",
    tagline: "The best-known standalone time tracker.",
    logoLetter: "T",
    color: "#E57CD8",
    intro:
      "Toggl Track is a widely used standalone time tracker known for its simple UI and generous free plan. Full Bench goes further by pairing time tracking with real project management and invoicing, so hours flow straight into money owed.",
    verdict:
      "Pick Full Bench if you need to plan, manage and monitor client projects end-to-end. Pick Toggl Track if you only need tracking and Pomodoro-style features.",
    table: [
      ["Free plan", "1 seat, 2 projects", "Up to 5 users"],
      ["Starting paid price", "$12 / seat / mo", "$9 / seat / mo"],
      ["Native invoicing", "Yes, built-in", "No"],
      ["Online payments (Stripe, PayPal)", "Yes", "No"],
      ["Project budgets & burn", "Yes", "Add-on / limited"],
      ["Timesheet approvals", "Yes", "Higher tier only"],
      ["Mobile apps", "iOS + Android", "iOS + Android"],
      ["Integrations", "50+", "100+"],
    ],
    pros: ["Invoicing + payments in one tool", "Real project budgeting and burn", "Approvals on standard plan"],
    cons: ["Fewer raw integrations than Toggl", "No Pomodoro-style timer"],
    faqs: [
      { q: "Is Full Bench a good Toggl Track alternative?", a: "Yes, especially if you invoice clients. Full Bench replaces Toggl plus a separate invoicing tool with a single workflow." },
      { q: "Toggl Track vs Full Bench, which is cheaper?", a: "Toggl's paid tier starts a few dollars lower per seat, but you'll pay extra for invoicing elsewhere. Full Bench is usually cheaper overall for billable teams." },
      { q: "Can I import my Toggl data into Full Bench?", a: "Yes. Full Bench supports CSV import for time entries, and both tools export in compatible formats." },
    ],
  },
  {
    slug: "clockify",
    name: "Clockify",
    tagline: "Free-forever tracker with unlimited users.",
    logoLetter: "C",
    color: "#03A9F4",
    intro:
      "Clockify is popular because it offers unlimited users on the free plan. The trade-off is a busier interface and paywalled essentials like approvals and invoicing. Full Bench keeps the core experience polished and includes invoicing out of the box.",
    verdict:
      "Pick Full Bench for a cleaner UI and end-to-end billing. Pick Clockify if you have a very large team and need free-forever tracking above all else.",
    table: [
      ["Free plan", "1 seat, 2 projects", "Unlimited users"],
      ["Starting paid price", "$12 / seat / mo", "$3.99 / seat / mo"],
      ["Native invoicing", "Yes", "Paid add-on"],
      ["Online payments", "Yes", "No"],
      ["Approvals", "Yes", "Higher tier"],
      ["UI polish", "Best-in-class", "Functional"],
      ["Mobile apps", "iOS + Android", "iOS + Android"],
    ],
    pros: ["Faster to set up", "Invoicing included", "Better reports out of the box"],
    cons: ["Free plan is more limited", "Fewer very-large-team features"],
    faqs: [
      { q: "Is Full Bench worth it over free Clockify?", a: "If you invoice clients or need approvals and budgets, yes. Full Bench replaces two tools with one." },
      { q: "Clockify vs Full Bench for consultants?", a: "Consulting teams usually prefer Full Bench because a single invoice tracks hours, sends the bill, and collects payment." },
    ],
  },
  {
    slug: "rescuetime",
    name: "RescueTime",
    tagline: "Automatic productivity tracking.",
    logoLetter: "R",
    color: "#5D9CEC",
    intro:
      "RescueTime automatically categorises the apps and sites you use to score your productivity. It doesn't do project-based billing. Full Bench is manual-by-design, so the data is client-ready and invoice-ready.",
    verdict: "Pick Full Bench for client work and invoicing. Pick RescueTime for personal focus and habits.",
    table: [
      ["Focus", "Client & project time", "Personal productivity"],
      ["Manual timer", "Yes", "Limited"],
      ["Invoicing", "Yes", "No"],
      ["Automatic app tracking", "No (privacy-first)", "Yes"],
      ["Best for", "Consultants & advisors", "Individuals"],
    ],
    pros: ["Client-ready timesheets", "Real invoicing", "Team features"],
    cons: ["No automatic categorisation"],
    faqs: [
      { q: "Can Full Bench replace RescueTime?", a: "Only if you're okay tracking manually. Some teams use both: RescueTime for personal focus, Full Bench for billable client work." },
    ],
  },
  {
    slug: "hubstaff",
    name: "Hubstaff",
    tagline: "Time tracker with employee monitoring.",
    logoLetter: "H",
    color: "#1F77E0",
    intro:
      "Hubstaff includes screenshots, activity levels and GPS. That's useful for some remote teams and uncomfortable for others. Full Bench takes a trust-first approach: no screenshots, no surveillance — just clean tracking, planning and reporting.",
    verdict: "Pick Full Bench for a trust-first, client-services workflow. Pick Hubstaff if you specifically need employee monitoring.",
    table: [
      ["Screenshots / monitoring", "No", "Yes"],
      ["Invoicing", "Yes", "Yes"],
      ["Payroll", "Via integrations", "Built-in"],
      ["Starting paid price", "$12 / seat / mo", "$7 / seat / mo"],
      ["Best for", "Consulting professionals", "BPO, remote ops"],
    ],
    pros: ["No surveillance", "Better reports for clients", "Cleaner UI"],
    cons: ["No monitoring or payroll"],
    faqs: [
      { q: "Does Full Bench track screenshots?", a: "No. Full Bench never takes screenshots or records screen activity." },
      { q: "Is Hubstaff better for remote teams?", a: "Only if monitoring is a requirement. Most consulting teams prefer Full Bench's trust-based model." },
    ],
  },
  {
    slug: "timely",
    name: "Timely",
    tagline: "AI-powered automatic time tracking.",
    logoLetter: "T",
    color: "#FFB020",
    intro:
      "Timely uses AI to draft time entries from your activity. It's clever but expensive, and invoicing lives in a separate product. Full Bench is a manual tracker with strong project management and invoicing built in — a simpler, cheaper stack for most consulting teams.",
    verdict: "Pick Full Bench for a lower price and a real billing workflow. Pick Timely if you want AI-drafted entries and don't mind manual invoicing elsewhere.",
    table: [
      ["AI-drafted entries", "No", "Yes"],
      ["Invoicing", "Yes", "No"],
      ["Starting paid price", "$12 / seat / mo", "$11 / seat / mo"],
      ["Ease of onboarding", "Very high", "Medium"],
    ],
    pros: ["Invoicing included", "Simpler onboarding", "Better project budgeting"],
    cons: ["No automatic drafting"],
    faqs: [
      { q: "Is Timely worth it over Full Bench?", a: "Only if AI-drafted entries matter more than integrated invoicing. For most consulting firms, Full Bench is the better value." },
    ],
  },
  {
    slug: "freshbooks",
    name: "FreshBooks",
    tagline: "Small-business accounting with time tracking.",
    logoLetter: "F",
    color: "#0075DD",
    intro:
      "FreshBooks is accounting software that happens to include time tracking. Full Bench is the opposite: a purpose-built tool to plan, manage and monitor client projects, with clean invoicing built in. Teams that live in timesheets choose Full Bench; teams that live in books choose FreshBooks.",
    verdict: "Pick Full Bench if time and project management is the core of your day. Pick FreshBooks if bookkeeping and taxes are.",
    table: [
      ["Primary purpose", "Project & time management", "Accounting"],
      ["Time tracking depth", "Excellent", "Basic"],
      ["Invoicing", "Yes", "Yes"],
      ["Bookkeeping / taxes", "Via Xero / MYOB", "Built-in"],
      ["Starting paid price", "$12 / seat / mo", "$21 / mo"],
    ],
    pros: ["Best-in-class tracker", "Better team & project tools", "Cleaner reports"],
    cons: ["No built-in accounting"],
    faqs: [
      { q: "Can Full Bench replace FreshBooks?", a: "For time tracking, project management and client invoicing, yes. For full bookkeeping, pair Full Bench with Xero or MYOB." },
    ],
  },
  {
    slug: "harvest",
    name: "Harvest",
    tagline: "The classic time tracker for creative teams.",
    logoLetter: "H",
    color: "#FA5D3F",
    intro:
      "Harvest is the long-standing time-tracker for design and creative teams. Full Bench is built for consulting professionals who bill on time and expense — with the planning, budget monitoring and invoicing they need in one place.",
    verdict: "Pick Full Bench if you're in consulting or advisory. Pick Harvest if you're a design studio wanting a mature, familiar tracker.",
    table: [
      ["Built for", "Consulting professionals", "Creative & design studios"],
      ["Time tracking", "Yes", "Yes"],
      ["Invoicing", "Yes", "Yes"],
      ["Project budget monitoring", "Yes, live", "Basic"],
      ["Starting paid price", "$12 / seat / mo", "$11 / seat / mo"],
    ],
    pros: ["Built for consulting workflows", "Live budget monitoring", "ANZ-first support"],
    cons: ["Younger product than Harvest", "Fewer marketplace integrations today"],
    faqs: [
      { q: "Is Full Bench a Harvest alternative?", a: "Yes. It's a direct alternative purpose-built for consulting professionals who bill on time and expense." },
      { q: "Harvest vs Full Bench for consultants?", a: "If you work on projects with budgets and claims, Full Bench is closer to how consultants already work." },
    ],
  },
];
